/**
 * ActionDialog — generic confirm-and-execute dialog for admin actions.
 *
 * Used by offboard, onboard, set-on-leave, and change-email. Shows a
 * confirmation prompt, runs the action on confirm, then displays the
 * per-system OperationResult inline.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Button, Spinner } from "@nu-assist/ui";
import type { OperationResult } from "./types";

interface ActionDialogProps {
  title: string;
  description: string;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  onConfirm: () => Promise<OperationResult>;
  onClose: () => void;
  children?: React.ReactNode;
}

type DialogState = "confirm" | "running" | "result";

export function ActionDialog({
  title,
  description,
  confirmLabel = "Confirm",
  confirmDisabled = false,
  onConfirm,
  onClose,
  children,
}: ActionDialogProps) {
  const [state, setState] = useState<DialogState>("confirm");
  const [result, setResult] = useState<OperationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setState("running");
    try {
      const r = await onConfirm();
      setResult(r);
      setState("result");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setState("result");
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface rounded-md shadow-lg w-full max-w-2xl p-6 font-sans">
        <h2 className="text-h2 text-text mb-2">{title}</h2>

        {state === "confirm" && (
          <>
            <p className="text-body text-text-muted mb-4">{description}</p>
            {children && <div className="mb-4">{children}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirm} disabled={confirmDisabled}>
                {confirmLabel}
              </Button>
            </div>
          </>
        )}

        {state === "running" && (
          <div className="flex items-center gap-3 py-4">
            <Spinner size="sm" />
            <span className="text-body text-text-muted">Running…</span>
          </div>
        )}

        {state === "result" && (
          <>
            {error ? (
              <p className="text-body text-red-600 mb-4">{error}</p>
            ) : result ? (
              <OperationResultView result={result} />
            ) : null}
            <div className="flex justify-end mt-4">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function OperationResultView({ result }: { result: OperationResult }) {
  const STEP_COLOR: Record<string, string> = {
    ok: "text-green-600",
    failed: "text-red-600",
    skipped: "text-text-muted",
    manual: "text-yellow-600",
    address_held_by_departed: "text-orange-500",
  };

  return (
    <div className="space-y-2">
      <p
        className={`text-body font-medium ${result.success ? "text-green-600" : "text-red-600"}`}
      >
        {result.success ? "Completed successfully" : "Completed with errors"}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-body">
          <tbody>
            {Object.entries(result.steps).map(([system, step]) => (
              <tr key={system} className="border-b border-border last:border-0">
                <td className="py-1 pr-4 capitalize text-text-muted whitespace-nowrap">{system}</td>
                <td className={`py-1 pr-4 whitespace-nowrap ${STEP_COLOR[step.status] ?? ""}`}>
                  {step.status}
                </td>
                <td className="py-1 text-text-muted text-sm">{step.message ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
